import { ConnectedAccountChargeStatus } from '@ankor-io/common/types/awx/wallet/connected.account.charge'

export type OrganizationPayFee = {
  id: string
  organizationId: string
  feeSource: FeeSource
  feeType: FeeType
  feeAmount: number
  updatedAt: string
  updatedByName: string
  updatedReason: string
}

/**
 * Per-connected-account fee configuration. Overrides the org-level `OrganizationPayFee`
 * for the same `FeeSource`. The owning organization is derived from the connected account.
 */
export type ConnectedAccountPayFee = {
  id: string
  awxConnectedAccountId: string
  feeSource: FeeSource
  feeType: FeeType
  feeAmount: number
  updatedAt: string
  updatedByName: string
  updatedReason: string
}

export enum FeeSource {
  CONVERSION = 'CONVERSION',
  TRANSFER = 'TRANSFER',
  DEPOSIT = 'DEPOSIT',
}

export enum FeeType {
  FIXED = 'FIXED',
  PERCENTAGE = 'PERCENTAGE',
}

export type Fee = {
  source: FeeSource
  type: FeeType
  amount: number
}

export type FeePayload = {
  // when undefined system default fees will be used
  id?: string
  feeSource: FeeSource
  feeType: FeeType
  feeAmount: number
}

export type UpdateOrganizationPayFeesBody = {
  fees: {
    feeSource: FeeSource
    feeType: FeeType
    feeAmount: number
    updatedByName: string
    updatedReason: string
    /**
     * When set, the fee is an override scoped to this connected account and is stored
     * against the connected account. When omitted/null the org-level fee is updated.
     */
    awxConnectedAccountId?: string
  }[]
}

export type OrganizationFeeWithDefault = {
  id?: string // undefined if using system default
  feeSource: FeeSource
  feeType: FeeType
  feeAmount: number
  /**
   * Undefined for org-level entries. Set for connected-account overrides, identifying
   * the connected account the override is scoped to.
   */
  awxConnectedAccountId?: string
  globalDefault: {
    feeType: FeeType
    feeAmount: number
  }
  isOverride: boolean // true if org (or connected account) has a custom value
  updatedAt?: string
  updatedByName?: string
  updatedReason?: string
}

export type OrganizationFeesPayload = {
  organizationId: string
  fees: OrganizationFeeWithDefault[]
}

export type SystemFeesPayload = {
  fees: FeePayload[]
}

// ---------------------------------------------------------------------------
// Connected-account fee overrides (service-to-gateway requests)
// ---------------------------------------------------------------------------

/**
 * Service-to-gateway request to create a connected-account fee override.
 * `feeAmount` is the raw config value as stored (fixed amount or percentage).
 */
export type CreateConnectedAccountPayFeeRequest = {
  awxConnectedAccountId: string
  feeSource: FeeSource
  feeType: FeeType
  feeAmount: string
  updatedByName: string
  updatedReason: string
}

/**
 * Service-to-gateway request to upsert a connected-account fee override.
 */
export type UpsertConnectedAccountPayFeeRequest = CreateConnectedAccountPayFeeRequest

/**
 * Service-to-gateway request to update a connected-account fee override by id.
 */
export type UpdateConnectedAccountPayFeeRequest = {
  id: string
  feeType: FeeType
  feeAmount: string
  updatedByName: string
  updatedReason: string
}

/**
 * Service-to-gateway request to delete a connected-account fee override.
 */
export type DeleteConnectedAccountPayFeeRequest = {
  awxConnectedAccountId: string
  feeSource: FeeSource
}

export type GetConnectedAccountPayFeeRequest = {
  id: string
}

export type ListConnectedAccountPayFeesByAccountRequest = {
  awxConnectedAccountId: string
}

export type ListConnectedAccountPayFeesByOrganizationRequest = {
  organizationId: string
}

/**
 * Service-to-service request to resolve the fee to apply for a source.
 * Precedence: connected-account override → organization config → platform default.
 */
export type ResolveFeeRequest = {
  feeSource: FeeSource
  organizationId: string
  awxConnectedAccountId?: string
}

// ---------------------------------------------------------------------------
// Platform-imposed charge realization
// ---------------------------------------------------------------------------

/**
 * A platform-imposed charge applied to a connected account. Snapshots the charge values
 * at application time; it does not reference the config row.
 * `amountCents` is stored negative (money not available), matching the expense sign convention.
 */
export type AWXConnectedAccountCharge = {
  id: string
  /**
   * The Airwallex Scale charge id. Also the join key for `charge.*` webhook events
   * (`AwxWebhookEventLog.sourceId`). The writer always sets it; nullable only as a
   * migration safety net on the column.
   */
  awxChargeId?: string
  awxConnectedAccountId: string
  feeSource: FeeSource
  feeType: FeeType
  /**
   * The fee config value that priced this charge, as resolved at application time
   * (a percentage like `0.2`, or a fixed amount). Snapshot: the config can change later.
   */
  feeAmount: number
  amountCents: number
  currencyCode: string
  /**
   * Status reduced from the charge webhook event log. `NEW` until the first event arrives.
   */
  status: ConnectedAccountChargeStatus
  reason?: string
  description?: string
  reference?: string
  createdAt: string
  updatedAt: string
}

export type GetAWXConnectedAccountChargeRequest = {
  id: string
}

/**
 * Request to look up the local charge row by its Airwallex charge id, guarded by the
 * connected account it belongs to. Used when reducing a `charge.*` webhook event and
 * when serving charge details.
 */
export type GetAWXConnectedAccountChargeByAwxChargeIdRequest = {
  awxChargeId: string
  awxConnectedAccountId: string
}

/**
 * Request to look up the local charge row linked to a deposit, via the deposit-owned
 * `charge_id` FK. Used to compose deposit detail payloads.
 */
export type GetAWXConnectedAccountChargeByDepositIdRequest = {
  depositId: string
}

export type CreateAWXConnectedAccountChargeRequest = {
  awxChargeId: string
  awxConnectedAccountId: string
  feeSource: FeeSource
  feeType: FeeType
  /** The fee config value that priced this charge, as resolved at application time */
  feeAmount: number
  amountCents: number
  currencyCode: string
  status: ConnectedAccountChargeStatus
  reason?: string
  description?: string
  reference?: string
}

export type UpdateAWXConnectedAccountChargeStatusRequest = {
  awxChargeId: string
  status: ConnectedAccountChargeStatus
}

export type ListAWXConnectedAccountChargesByAccountRequest = {
  awxConnectedAccountId: string
}

export type ListAWXConnectedAccountChargesByOrganizationRequest = {
  organizationId: string
}

export type ListAWXConnectedAccountChargesByVesselRequest = {
  vesselId: string
}
